import { View } from "react-native";
import React from "react";
import MainContainer from "components/MainContainer";
import { AccountStackParamList } from "navigation/types";
import useCurrentUser from "hooks/useCurrentUser";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import UserInfo from "screens/Account/components/UserInfo";
import { AccountMemberDetails } from "features/types";
import { Text } from "@ui-kitten/components";
import ProfileAvatar from "components/ProfileAvatar";
import env from "config/env";
import Spacer from "components/Spacer";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useAccount } from "hooks/useAccount";
import i18n from "plugins/localization";
import { getUserName, initial } from "utils/userUtils";

const RenderUserInfo: React.FC<{
  member: AccountMemberDetails;
  showUserImage?: boolean;
}> = ({ member, showUserImage = true }) => {
  const {
    user: { imageIdentifier },
  } = member;
  const userImageURL = `${env.fileUrl}/${imageIdentifier}`;
  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      {showUserImage && (
        <View style={{ marginRight: 10 }}>
          <ProfileAvatar
            size={50}
            source={{ uri: userImageURL }}
            hasImageIdentifier={imageIdentifier !== null}
            initial={initial(member.user)}
          />
        </View>
      )}
      <View>
        {member.type === "PRIMARY" && (
          <Text category="c1">{i18n.t("common.accountOwner")}</Text>
        )}
        <Text>{getUserName(member)}</Text>
      </View>
    </View>
  );
};

const AccountDetails = () => {
  const navigation = useNavigation<NavigationProp<AccountStackParamList>>();
  const { currentUser, userImageURL, currentMember } = useCurrentUser();
  const { account } = useAccount();

  if (!currentMember || !currentUser) return null;

  const otherMembersInAccount =
    account?.members.filter((member) => member.user.id !== currentUser.id) ||
    [];

  return (
    <MainContainer
      grayBackground
      goBackAction={() => navigation.navigate("AccountScreen")}
    >
      <View style={{ padding: 15 }}>
        <BaseCard>
          {currentMember && (
            <UserInfo
              userImageURL={userImageURL}
              currentMember={currentMember}
            />
          )}
        </BaseCard>

        <Spacer />

        <BaseCard title={i18n.t("screens.AccountDetails.personalInformation")}>
          <BaseCardListItem
            title={
              <RenderUserInfo member={currentMember} showUserImage={false} />
            }
            onPress={() => navigation.navigate("EditProfile")}
          ></BaseCardListItem>
        </BaseCard>

        <Spacer />

        {otherMembersInAccount.length > 0 && (
          <BaseCard
            title={i18n.t(
              "screens.AccountDetails.additionalMembersInformation"
            )}
          >
            {otherMembersInAccount?.map((member) => (
              <BaseCardListItem
                key={member.id}
                title={<RenderUserInfo member={member} />}
                showArrow={false}
              ></BaseCardListItem>
            ))}
          </BaseCard>
        )}
      </View>
    </MainContainer>
  );
};

export default AccountDetails;
